import React, { useCallback, useEffect, useMemo, useState } from "react";

import { api } from "../../../../api/apiClient";
import { notify } from "../../../components-ui/toast";
import {
  RegistrationMemberType,
  ServiceEnrollmentDto,
  ServiceEnrollmentRoleDto,
  ServiceRoleDto,
} from "../../../types/types";

import "../../../../css/forms.css";
import "../../../../css/buttons.css";
import "./ServiceEnrollmentCard.css";

interface ServiceEnrollmentMembershipCardProps {
  serviceEnrollmentId: number;
  serviceId: number;
  memberTypes?: RegistrationMemberType[];
  readonly?: boolean;
  initiallyEditing?: boolean;
  onClose?: () => void;
  onSaved?: (enrollment: ServiceEnrollmentDto) => void | Promise<void>;
}

const MEMBER_TYPE_OPTIONS: Array<{
  value: RegistrationMemberType;
  label: string;
}> = [
  { value: "ServiceUser", label: "Service user" },
  { value: "PrimaryContact", label: "Primary contact" },
  { value: "Volunteer", label: "Volunteer" },
  { value: "Staff", label: "Staff" },
];

const normalizeMemberTypes = (
  memberTypes?: RegistrationMemberType[],
): RegistrationMemberType[] =>
  memberTypes?.length ? [...new Set(memberTypes)] : ["ServiceUser"];

export default function ServiceEnrollmentMembershipCard({
  serviceEnrollmentId,
  serviceId,
  memberTypes,
  readonly = false,
  initiallyEditing = false,
  onClose,
  onSaved,
}: ServiceEnrollmentMembershipCardProps) {
  const [editing, setEditing] = useState(initiallyEditing);
  const [availableRoles, setAvailableRoles] = useState<ServiceRoleDto[]>([]);
  const [selectedMemberTypes, setSelectedMemberTypes] = useState<
    RegistrationMemberType[]
  >(() => normalizeMemberTypes(memberTypes));
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const activeRoles = useMemo(
    () => availableRoles.filter((role) => role.isActive),
    [availableRoles],
  );
  const hasOperationalMemberType = selectedMemberTypes.some(
    (memberType) => memberType === "Volunteer" || memberType === "Staff",
  );

  useEffect(() => {
    if (!editing) {
      setSelectedMemberTypes(normalizeMemberTypes(memberTypes));
    }
  }, [editing, memberTypes]);

  const loadRoles = useCallback(async () => {
    try {
      setLoading(true);
      const [roles, assigned] = await Promise.all([
        api<ServiceRoleDto[]>(`/api/services/${serviceId}/roles`),
        api<ServiceEnrollmentRoleDto[]>(
          `/api/service-enrollments/${serviceEnrollmentId}/roles`,
        ),
      ]);

      setAvailableRoles(roles ?? []);
      const availableRoleIds = new Set(
        (roles ?? []).filter((role) => role.isActive).map((role) => role.serviceRoleId),
      );
      setSelectedRoleIds(
        (assigned ?? [])
          .filter((role) => role.isActive && availableRoleIds.has(role.serviceRoleId))
          .map((role) => role.serviceRoleId),
      );
    } catch (error) {
      console.error("Failed to load service membership", error);
      notify.error("Failed to load service membership.");
    } finally {
      setLoading(false);
    }
  }, [serviceEnrollmentId, serviceId]);

  useEffect(() => {
    if (initiallyEditing) void loadRoles();
  }, [initiallyEditing, loadRoles]);

  const startEditing = async () => {
    setSelectedMemberTypes(normalizeMemberTypes(memberTypes));
    setEditing(true);
    await loadRoles();
  };

  const toggleMemberType = (
    memberType: RegistrationMemberType,
    checked: boolean,
  ) => {
    const next = checked
      ? [...selectedMemberTypes, memberType]
      : selectedMemberTypes.filter((value) => value !== memberType);

    if (next.length > 0) {
      setSelectedMemberTypes([...new Set(next)]);
    }
  };

  const toggleRole = (serviceRoleId: number, checked: boolean) => {
    const next = checked
      ? [...selectedRoleIds, serviceRoleId]
      : selectedRoleIds.filter((id) => id !== serviceRoleId);
    setSelectedRoleIds([...new Set(next)]);
  };

  const save = async () => {
    try {
      setSaving(true);
      const enrollment = await api<ServiceEnrollmentDto>(
        `/api/service-enrollments/${serviceEnrollmentId}/member-types`,
        {
          method: "PUT",
          body: { memberTypes: selectedMemberTypes },
        },
      );

      await api<ServiceEnrollmentRoleDto[]>(
        `/api/service-enrollments/${serviceEnrollmentId}/roles`,
        {
          method: "PUT",
          body: {
            serviceRoleIds: hasOperationalMemberType ? selectedRoleIds : [],
          },
        },
      );

      setSelectedMemberTypes(normalizeMemberTypes(enrollment.memberTypes));
      setEditing(false);
      notify.success("Service membership saved.");
      await onSaved?.(enrollment);
      onClose?.();
    } catch (error) {
      console.error("Failed to save service membership", error);
      notify.error("Failed to save service membership.");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return readonly ? null : (
      <div className="service-enrollment-membership-summary">
        <button
          type="button"
          className="link-button"
          onClick={startEditing}
        >
          Manage membership
        </button>
      </div>
    );
  }

  return (
    <div className="service-enrollment-membership-editor">
      <div className="service-enrollment-roles-title">Member types</div>
      <div className="field-hint">
        A person can have more than one member type for the same service.
      </div>
      <div className="service-enrollment-membership-options">
        {MEMBER_TYPE_OPTIONS.map((option) => {
          const checked = selectedMemberTypes.includes(option.value);
          return (
            <label key={option.value} className="option-item">
              <input
                type="checkbox"
                checked={checked}
                disabled={saving || (checked && selectedMemberTypes.length === 1)}
                onChange={(event) =>
                  toggleMemberType(option.value, event.target.checked)
                }
              />
              {option.label}
            </label>
          );
        })}
      </div>

      {hasOperationalMemberType && (
        <div className="service-enrollment-roles-section">
          <div className="service-enrollment-roles-title">Service roles</div>
          <div className="field-hint">
            Select the duties this volunteer or staff member can perform.
          </div>
          {loading ? (
            <div className="field-hint">Loading roles...</div>
          ) : activeRoles.length === 0 ? (
            <div className="field-hint">
              No active service roles have been set up for this service.
            </div>
          ) : (
            <div className="service-enrollment-membership-options">
              {activeRoles.map((role) => (
                <label key={role.serviceRoleId} className="option-item">
                  <input
                    type="checkbox"
                    checked={selectedRoleIds.includes(role.serviceRoleId)}
                    disabled={saving}
                    onChange={(event) =>
                      toggleRole(role.serviceRoleId, event.target.checked)
                    }
                  />
                  {role.name}
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="button-row button-row-left">
        <button
          type="button"
          className="action-button compact"
          onClick={save}
          disabled={saving || loading}
        >
          {saving ? "Saving..." : "Save membership"}
        </button>
        <button
          type="button"
          className="action-button compact secondary"
          onClick={() => { setEditing(false); onClose?.(); }}
          disabled={saving}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
